import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CakeCard from '../components/CakeCard';
import { fetchCakes, fetchCategories, filterCakesByCategory } from '../data/cakes';
import { api } from '../utils/api';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const [cakes, setCakes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredCakes, setFilteredCakes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCakes();
  }, []);

  useEffect(() => {
    const filtered = filterCakesByCategory(cakes, selectedCategory);
    setFilteredCakes(filtered);
  }, [selectedCategory, cakes]);

  const [debugError, setDebugError] = useState(null);

  const loadCakes = async () => {
    setLoading(true);
    setDebugError(null);
    
    try {
      const data = await fetchCakes();
      console.log('Cakes loaded:', data);
      
      if (!data || data.length === 0) {
        // Fetch raw response to see what we're getting
        try {
          const rawData = await api.get('/cakes.php');
          setDebugError('No cakes found.\n\nRaw Response:\n' + JSON.stringify(rawData, null, 2).substring(0, 1000));
        } catch (e) {
          setDebugError('No cakes found. Failed to get raw response: ' + e.message);
        }
      }
      
      setCakes(data);
      
      // Fetch categories from API (includes subcategories)
      const cats = await fetchCategories();
      setCategories(cats);
    } catch (err) {
      console.error('Error loading cakes:', err);
      setDebugError('Error: ' + (err.message || JSON.stringify(err)));
      setCakes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleAddToCart = (cake) => {
    navigate(`/cake/${cake.id}`);
  };

  return (
    <div className="home-page">
      {debugError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: 'rgba(255,0,0,0.9)',
          color: 'white',
          padding: '60px 20px 20px',
          zIndex: 9999,
          fontSize: '11px',
          maxHeight: '50vh',
          overflow: 'auto',
          wordBreak: 'break-word'
        }}>
          <h3 style={{margin: '0 0 10px 0'}}>Debug Error:</h3>
          <pre style={{margin: 0, whiteSpace: 'pre-wrap'}}>{debugError}</pre>
          <button onClick={loadCakes} style={{padding: '10px', marginTop: '10px', color: 'black'}}>Retry</button>
          <button onClick={() => setDebugError(null)} style={{padding: '10px', marginTop: '10px', marginLeft: '10px', color: 'black'}}>Close</button>
        </div>
      )}
      <Header title="Supple Bakes" />
      
      {/* Hero Section */}
      <section className="hero gradient-primary">
        <div className="hero-content">
          <h2 className="hero-title fade-in">Freshly Baked, Made with Love</h2>
          <p className="hero-subtitle fade-in">Order your favorite cakes and treats online</p>
        </div>
      </section>

      {/* Categories */}
      <section className="categories-section">
        <div className="container">
          {/* Main Categories */}
          <div className="categories-scroll">
            <button
              className={`category-chip ripple ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => handleCategoryChange('all')}
            >
              All Cakes
            </button>
            {categories.map(cat => {
              // Check if this category or any of its subcategories is selected
              const isActive = selectedCategory === cat.id || 
                (cat.subcategories && cat.subcategories.some(sub => sub.id === selectedCategory));
              
              return (
                <button
                  key={cat.id}
                  className={`category-chip ripple ${isActive ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(cat.id)}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>
          
          {/* Subcategories - Show only when parent category or its subcategory is selected */}
          {(() => {
            // First check if selected is a parent category
            let selectedParent = categories.find(cat => cat.id === selectedCategory);
            
            // If not found, check if selected is a subcategory and find its parent
            if (!selectedParent) {
              selectedParent = categories.find(cat => 
                cat.subcategories && cat.subcategories.some(sub => sub.id === selectedCategory)
              );
            }
            
            if (selectedParent && selectedParent.subcategories && selectedParent.subcategories.length > 0) {
              return (
                <div className="subcategories-row">
                  <button
                    className={`category-chip ripple subcategory ${selectedCategory === selectedParent.id ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(selectedParent.id)}
                  >
                    All {selectedParent.name}
                  </button>
                  {selectedParent.subcategories.map(subcat => (
                    <button
                      key={subcat.id}
                      className={`category-chip ripple subcategory ${selectedCategory === subcat.id ? 'active' : ''}`}
                      onClick={() => handleCategoryChange(subcat.id)}
                    >
                      {subcat.name}
                    </button>
                  ))}
                </div>
              );
            }
            return null;
          })()}
        </div>
      </section>

      {/* Products Grid */}
      <section className="products-section">
        <div className="container">
          <h3 className="section-title">
            {selectedCategory === 'all' ? 'All Cakes' : categories.find(c => c.id === selectedCategory)?.name}
          </h3>
          
          {loading ? (
            <div className="loading-grid">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton" style={{ height: '200px', marginBottom: '1rem' }}></div>
                  <div className="skeleton" style={{ height: '20px', width: '60%', marginBottom: '0.5rem' }}></div>
                  <div className="skeleton" style={{ height: '16px', width: '100%', marginBottom: '0.5rem' }}></div>
                  <div className="skeleton" style={{ height: '16px', width: '80%' }}></div>
                </div>
              ))}
            </div>
          ) : filteredCakes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🎂</div>
              <h3>No cakes found</h3>
              <p>Try selecting a different category</p>
            </div>
          ) : (
            <div className="cakes-grid">
              {filteredCakes.map(cake => (
                <CakeCard 
                  key={cake.id} 
                  cake={cake} 
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;
